/**
 * Canal (Moderno / Tradicional) de un piso de venta.
 *
 * Los pisos no guardan el canal: se deduce del `storeType`. Los valores
 * `nacional|regional|independiente` son cadenas MODERNO; cualquier otro slug
 * (bodega, licorería, etc.) pertenece al canal tradicional. Es el mismo criterio
 * que usa el export de auditores y la resolución de productos en el servicio.
 */

import { storeIdOf } from './tradeInvestment';

export const MODERN_STORE_TYPES = ['nacional', 'regional', 'independiente'];

export const ALL_CHANNELS = 'all';

/** Opciones del filtro de canal (incluye "Todos"). */
export const CHANNEL_FILTER_OPTIONS = [
    { value: ALL_CHANNELS, label: 'Todos los canales' },
    { value: 'moderno', label: 'Moderno' },
    { value: 'tradicional', label: 'Tradicional' }
];

/** 'moderno' | 'tradicional' a partir del `storeType` del piso. */
export function getStoreChannel(store) {
    const storeType = String(store?.storeType || '').trim().toLowerCase();
    return MODERN_STORE_TYPES.includes(storeType) ? 'moderno' : 'tradicional';
}

/** Etiqueta legible del canal. */
export function getChannelLabel(channel) {
    return channel === 'moderno' ? 'Moderno' : channel === 'tradicional' ? 'Tradicional' : '';
}

/** `true` cuando el filtro realmente restringe (no es "Todos"). */
export function isChannelFiltered(channel) {
    return !!channel && channel !== ALL_CHANNELS;
}

/** Pisos del canal indicado. Sin filtro devuelve la lista original. */
export function filterStoresByChannel(stores, channel) {
    if (!isChannelFiltered(channel)) return stores || [];
    return (stores || []).filter((store) => getStoreChannel(store) === channel);
}

/**
 * Copia de la contratación/campaña con solo los pisos del canal. Como el resto
 * de la pantalla (inversión, analíticas, galería, tabla) parte de `stores`,
 * filtrar aquí propaga el filtro a todo el detalle.
 */
export function filterEntityByChannel(entity, channel) {
    if (!entity || !isChannelFiltered(channel)) return entity;
    return { ...entity, stores: filterStoresByChannel(entity.stores, channel) };
}

/** Visitas/checks que pertenecen a alguno de los pisos indicados. */
export function filterChecksByStores(checks, stores) {
    const allowed = new Set((stores || []).map(storeIdOf).filter(Boolean));
    return (checks || []).filter((check) => allowed.has(storeIdOf(check?.storeId)));
}

/** Conteo de pisos por canal, para mostrarlo junto al filtro. */
export function countStoresByChannel(stores) {
    return (stores || []).reduce(
        (counts, store) => {
            counts[getStoreChannel(store)] += 1;
            return counts;
        },
        { moderno: 0, tradicional: 0 }
    );
}
